
import type { PVRunCreateRequest } from "../services/pvSizing";
import { downloadRunJson } from "../services/runExport";
import { PVResultPanel } from "../components/PVResultPanel";
import { PVResultSummary } from "../components/PVResultSummary";
import { PVStudyForm } from "../components/PVStudyForm";
import { PVWarningPanel } from "../components/PVWarningPanel";
import { RunTraceabilityPanel } from "../components/RunTraceabilityPanel";
import { SldLinkPanel } from "../components/SldLinkPanel";
import { SourceSizingSwitch } from "../components/SourceSizingSwitch";
import { StudyProjectLine } from "../components/StudyProjectLine";
import { useSldElements } from "../hooks/useSldElements";
import { usePVSizing } from "../hooks/usePVSizing";

function describeError(error: unknown): string {
  if (error instanceof Error && error.message.trim().length > 0) {
    return error.message;
  }
  return "The PV sizing study could not be completed.";
}

// Study-page layout (Master Prompt v2.1 section 19): collapsible inputs beside
// a result-first column - summary, warnings, traceability, then detail tables.
export function PVSizingPage() {
  const { calculate, reset, result, run, error, isPending, isError } = usePVSizing();
  // The active revision's single-line diagram, for the source picker (A28 (d)).
  const { elements: sldElements } = useSldElements();

  // Errors are surfaced through the hook state below, not thrown into the form.
  async function handleSubmit(payload: PVRunCreateRequest): Promise<void> {
    try {
      await calculate(payload);
    } catch {
      // Intentionally empty: isError / error already describe the failure.
    }
  }

  return (
    <main data-pv-sizing-page>
      <SourceSizingSwitch />
      <header>
        <p>EOS-03 · PV Sizing</p>
        <h1>PV sizing</h1>
        <p>
          Size the inverters and the PV array against the required AC output, the module and
          inverter data and the site temperatures of the project design basis, using the
          validated KES Electrical OS calculation service. Annual energy yield is not
          evaluated.
        </p>
      </header>

      <div data-study-layout>
        <section aria-labelledby="pv-study-inputs-heading">
          <details open data-study-inputs>
            <summary>
              <h2 id="pv-study-inputs-heading">Inputs</h2>
            </summary>
            <StudyProjectLine />
            <PVStudyForm
              disabled={isPending}
              onSubmit={handleSubmit}
              sldElements={sldElements}
            />
          </details>
        </section>

        <section aria-labelledby="pv-study-results-heading" data-study-results>
          <h2 id="pv-study-results-heading">Results</h2>

          {isPending ? (
            <p role="status" data-calculation-state="pending">
              Calculating the PV size…
            </p>
          ) : null}

          {isError ? (
            <p role="alert" data-calculation-state="error">
              {describeError(error)}
            </p>
          ) : null}

          {result ? (
            <div data-calculation-state="success">
              <PVResultSummary result={result} />
              {run ? <SldLinkPanel link={run.sld_link} /> : null}
              <PVWarningPanel warnings={result.warnings} />
              {run ? (
                <RunTraceabilityPanel
                  run={run}
                  onExport={() =>
                    downloadRunJson(
                      { run, result },
                      `${run.calculation_key}-rev${run.revision_number}.json`,
                    )
                  }
                />
              ) : null}
              <PVResultPanel result={result} />
              <p>
                <button type="button" onClick={reset}>
                  Clear results
                </button>
              </p>
            </div>
          ) : null}

          {!isPending && !isError && !result ? (
            <p data-calculation-state="idle">Submit the inputs to size a PV system.</p>
          ) : null}
        </section>
      </div>

      <section aria-labelledby="pv-study-review-heading">
        <h2 id="pv-study-review-heading">Engineering review required</h2>
        <p>
          Calculation results are engineering evidence, not an automatic compliance
          declaration. Confirm the governing project basis, the required AC output, the module
          and inverter datasheet values, the site temperatures, the target DC/AC ratio, the
          rating schedule and every growth, margin and derating factor before approval or
          issue.
        </p>
      </section>
    </main>
  );
}

export default PVSizingPage;
